import { Injectable } from "@angular/core";
import { CommunityDTO } from "../models/communityDTO";
import { db, InspectionJob } from "../../../../../core/database/db";
import { CONSTANTS } from "../../../../../constants";
import { DateUtilsService } from "../../../../../shared/services/date.service";

@Injectable({ providedIn: "root" })
export class AllMyLotsService {

  constructor(private dateUtils: DateUtilsService) { }

  async getAllMyLots(): Promise<CommunityDTO[]> {
    const lots = await db.lot.toArray();

    const localJobs = await db.inspectionJob.toArray();
    const jobsByLot = localJobs.reduce((prev, current) => {
      if (!prev[current.lotId]) {
        prev[current.lotId] = [];
      }

      prev[current.lotId].push(current);
      return prev;
    }, {} as { [key: number]: InspectionJob[] });

    const jobIds = localJobs.map(job => job.id);
    const localIssues = await db.jobIssue
      .where("jobId")
      .anyOf(jobIds)
      .toArray();

    const issuesCountMap: Record<number, number> = {};

    for (const issue of localIssues) {
      if (!issuesCountMap[issue.jobId]) {
        issuesCountMap[issue.jobId] = 1;
      } else {
        issuesCountMap[issue.jobId] += 1;
      }
    }

    // Fetch all BQ8 jobs and create a map by lotId
    const bq8Jobs = localJobs.filter(job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ8);
    const bq8JobByLotId = new Map<number, InspectionJob>();
    for (const bq8Job of bq8Jobs) {
      bq8JobByLotId.set(bq8Job.lotId, bq8Job);
    }

    const communityDTOs: CommunityDTO[] = Object.values(
      lots.reduce((acc: { [key: string]: CommunityDTO }, lot) => {
        const communityId = lot.externalFinancialCommunityId;

        if (!acc[communityId]) {
          acc[communityId] = {
            externalFinancialCommunityId: communityId,
            salesCommunityName: lot.salesCommunityName,
            financialCommunityName: lot.financialCommunityName,
            lots: []
          };
        }

        const address = `Lot ${lot.lotBlock} - ${lot.streetAddress}`;

        const issueCount = jobsByLot[lot.id]?.reduce((aggregator, current) => {
          if (!issuesCountMap[current.id]) {
            return aggregator;
          }
          return aggregator + issuesCountMap[current.id];
        }, 0) ?? 0;

        // Find BQ8 job for this lot and calculate days countdown
        const bq8Job = bq8JobByLotId.get(lot.id);
        let bq8DaysCountdown: number | undefined;
        if (bq8Job && bq8Job.scheduledDate) {
          bq8DaysCountdown = this.dateUtils.daysUntil(bq8Job.scheduledDate);
        }

        acc[communityId].lots.push({
          id: lot.id,
          jobIds: jobsByLot[lot.id]?.map(job => job.id) ?? [],
          address,
          openIssuesCount: issueCount,
          ...(bq8DaysCountdown !== undefined && { bq8DaysCountdown })
        });
        return acc;
      }, {} as Record<number, CommunityDTO>)
    );

    return communityDTOs.sort((a, b) => {
      const sortA = a.financialCommunityName ?? a.salesCommunityName;
      const sortB = b.financialCommunityName ?? b.salesCommunityName;
      return sortA.localeCompare(sortB);
    });
  }
}
